import { Skeleton } from 'v5/design-system/Skeleton';
import { cn } from 'v5/platform/dom/cn';
import { useSkeletonFade } from '../skeletonFade';

/** A line of text as a bar: the height of the real line, which an invisible letter holds. */
function TextBar({ className, small }: { className: string; small?: boolean }) {
  return (
    <div
      className={cn(
        'relative',
        small ? 'mt-0.5 text-sm' : 'font-medium leading-snug'
      )}
    >
      <span className="invisible">M</span>
      <Skeleton className={cn('absolute inset-y-[0.2em] left-0', className)} />
    </div>
  );
}

export interface ResourceAsideRowSkeletonProps {
  /** What stands before the text: a status dot, or an avatar. */
  lead?: 'dot' | 'avatar';
  secondary?: boolean;
  trailing?: 'text' | 'badge';
}

/** A row of the panel's lists before its data is there, as tall as the row it becomes. */
export function ResourceAsideRowSkeleton({
  lead,
  secondary = true,
  trailing
}: ResourceAsideRowSkeletonProps) {
  const fade = useSkeletonFade();
  return (
    <div
      aria-hidden
      className={cn('flex items-center gap-4 px-4 py-3', fade.className)}
      style={fade.style}
    >
      {lead === 'dot' && <Skeleton className="size-3 shrink-0 rounded-full" />}
      {lead === 'avatar' && <Skeleton className="size-8 shrink-0 rounded-md" />}
      <div className="min-w-0 flex-1">
        <TextBar className="w-28" />
        {secondary && <TextBar small className="w-20" />}
      </div>
      {trailing === 'text' && <Skeleton className="h-3 w-12" />}
      {trailing === 'badge' && (
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-14 rounded" />
          <Skeleton className="size-4 rounded" />
        </div>
      )}
    </div>
  );
}

/** Rows as they will be, for a section whose items are loading. */
export function ResourceAsideRowsSkeleton({
  rows = 2,
  ...row
}: ResourceAsideRowSkeletonProps & { rows?: number }) {
  return (
    <div className="pb-1">
      {Array.from({ length: rows }, (_, index) => (
        <ResourceAsideRowSkeleton key={index} {...row} />
      ))}
    </div>
  );
}
